import * as React from 'react'
import {
  mergeParamDraftsById,
  normalizeHeaderDrafts,
  normalizeParamDrafts,
  type HeaderDraft,
  type ParamDraft,
} from '@/lib/tool-http-schema'
import type { ToolVisibility } from '@/types/tool'

export type ToolType = 'webhook' | 'mcp_server'

export type ToolDraft = {
  name: string
  display_name: string
  description: string
  is_enabled: boolean
  http_config: {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    url: string
    request_headers: HeaderDraft[]
    path_params_schema: ParamDraft[]
    query_params_schema: ParamDraft[]
  }
  timeout_seconds: number
  retry_count: number
  mcp_url: string
  mcp_transport: 'http_jsonrpc' | 'streamable_http'
  mcp_auth_type: 'none' | 'bearer' | 'header'
  mcp_auth_header_name: string
  mcp_auth_secret_id: string
  emoji: string
  classification: ToolVisibility
}

export type ToolStudioPersistedDraft = {
  toolType: ToolType
  step: number
  visitedSteps: number[]
  draft: ToolDraft
  editorMode?: string
  jsonDraft?: string
}

const TOOL_STUDIO_DRAFT_STORAGE_KEY = 'tool-studio-draft-v1'

const WEBHOOK_STEP_TITLES = ['Identidad', 'Ejecución', 'Parámetros', 'Revisión']
const MCP_STEP_TITLES = ['Identidad', 'Ejecución', 'Revisión']

export function getStepTitlesForType(toolType: ToolType): string[] {
  return toolType === 'mcp_server' ? MCP_STEP_TITLES : WEBHOOK_STEP_TITLES
}

export function getTotalStepsForType(toolType: ToolType): number {
  return getStepTitlesForType(toolType).length
}

export function normalizeStudioVisibility(visibility: ToolVisibility | undefined): Exclude<ToolVisibility, 'mixed'> {
  return visibility === 'internal' || visibility === 'mixed' ? 'internal' : 'external'
}

export function createInitialToolDraft(): ToolDraft {
  return {
    name: '',
    display_name: '',
    description: '',
    is_enabled: true,
    http_config: { method: 'GET', url: '', request_headers: [], path_params_schema: [], query_params_schema: [] },
    timeout_seconds: 30,
    retry_count: 0,
    mcp_url: '',
    mcp_transport: 'http_jsonrpc',
    mcp_auth_type: 'none',
    mcp_auth_header_name: 'X-API-Key',
    mcp_auth_secret_id: '',
    emoji: '',
    classification: 'external',
  }
}

function isToolType(value: unknown): value is ToolType {
  return value === 'webhook' || value === 'mcp_server'
}

export function loadToolStudioPersistedDraft(): ToolStudioPersistedDraft | null {
  try {
    const raw = localStorage.getItem(TOOL_STUDIO_DRAFT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ToolStudioPersistedDraft>
    if (!parsed || typeof parsed !== 'object') return null
    if (!isToolType(parsed.toolType)) return null
    if (!parsed.draft || typeof parsed.draft !== 'object') return null
    const step = Number(parsed.step)
    const totalSteps = getTotalStepsForType(parsed.toolType)
    const safeStep = Number.isFinite(step) ? Math.min(Math.max(step, 1), totalSteps) : 1
    const visitedSteps = Array.isArray(parsed.visitedSteps)
      ? parsed.visitedSteps
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value >= 1 && value <= totalSteps)
      : [1]
    const persistedDraft = parsed.draft as Partial<ToolDraft>
    const initialDraft = createInitialToolDraft()
    const persistedHttpConfig = persistedDraft.http_config
    const normalizedHttpConfig =
      persistedHttpConfig && typeof persistedHttpConfig === 'object'
        ? {
            method:
              (persistedHttpConfig as { method?: ToolDraft['http_config']['method'] }).method
              || initialDraft.http_config.method,
            url: String((persistedHttpConfig as { url?: unknown }).url ?? initialDraft.http_config.url),
            request_headers: normalizeHeaderDrafts(persistedHttpConfig as ToolDraft['http_config']),
            path_params_schema: [],
            query_params_schema: mergeParamDraftsById(
              normalizeParamDrafts((persistedHttpConfig as { path_params_schema?: unknown }).path_params_schema),
              normalizeParamDrafts((persistedHttpConfig as { query_params_schema?: unknown }).query_params_schema),
              normalizeParamDrafts((persistedHttpConfig as { request_body_schema?: unknown }).request_body_schema)
            ),
          }
        : initialDraft.http_config

    const normalizedDraft: ToolDraft = {
      ...initialDraft,
      ...persistedDraft,
      name: String(persistedDraft.name ?? initialDraft.name),
      display_name: String(persistedDraft.display_name ?? initialDraft.display_name),
      description: String(persistedDraft.description ?? initialDraft.description),
      is_enabled: persistedDraft.is_enabled !== undefined ? Boolean(persistedDraft.is_enabled) : initialDraft.is_enabled,
      http_config: normalizedHttpConfig,
      timeout_seconds: Number(persistedDraft.timeout_seconds ?? initialDraft.timeout_seconds) || initialDraft.timeout_seconds,
      retry_count: Number(persistedDraft.retry_count ?? initialDraft.retry_count) || initialDraft.retry_count,
      mcp_url: String(persistedDraft.mcp_url ?? initialDraft.mcp_url),
      mcp_transport:
        persistedDraft.mcp_transport === 'streamable_http' || persistedDraft.mcp_transport === 'http_jsonrpc'
          ? persistedDraft.mcp_transport
          : initialDraft.mcp_transport,
      mcp_auth_type:
        persistedDraft.mcp_auth_type === 'bearer'
          || persistedDraft.mcp_auth_type === 'header'
          || persistedDraft.mcp_auth_type === 'none'
          ? persistedDraft.mcp_auth_type
          : initialDraft.mcp_auth_type,
      mcp_auth_header_name: String(persistedDraft.mcp_auth_header_name ?? initialDraft.mcp_auth_header_name),
      mcp_auth_secret_id: String(
        (persistedDraft as Partial<{ mcp_auth_secret_id: string }>).mcp_auth_secret_id
        ?? initialDraft.mcp_auth_secret_id
      ),
      emoji: String(persistedDraft.emoji ?? initialDraft.emoji),
      classification: normalizeStudioVisibility(persistedDraft.classification),
    }

    return {
      toolType: parsed.toolType,
      step: safeStep,
      visitedSteps: visitedSteps.length > 0 ? Array.from(new Set(visitedSteps)) : [1],
      draft: normalizedDraft,
    }
  } catch {
    return null
  }
}

export function saveToolStudioPersistedDraft(payload: ToolStudioPersistedDraft) {
  try {
    localStorage.setItem(TOOL_STUDIO_DRAFT_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Ignore quota/storage errors; persistence is best-effort only.
  }
}

export function clearToolStudioPersistedDraft() {
  try {
    localStorage.removeItem(TOOL_STUDIO_DRAFT_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export function useToolStudioPersistedState() {
  return React.useMemo(() => loadToolStudioPersistedDraft(), [])
}
